import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Header, SortDirection } from '@tanstack/react-table';
import { ArrowDown, ArrowUp } from 'lucide-react';

interface MobileSummaryHeaderCellProps<TData> {
  headers: Header<TData, unknown>[];
}

/**
 * Get the sort direction that will result from clicking toggleMultiSort.
 * - If primary sort (index 0) and asc: will become desc
 * - Otherwise: will become asc (either toggled or promoted to primary)
 */
function getSortResultDirection(
  sort: false | SortDirection,
  sortIndex: number,
): 'asc' | 'desc' {
  if (sortIndex === 0 && sort === 'asc') {
    return 'desc';
  }
  return 'asc';
}

/**
 * Header cell for the mobile summary column.
 *
 * Shows column names (first normal, rest muted) with a dropdown
 * menu for sorting by any of the contained columns.
 */
export function MobileSummaryHeaderCell<TData>({
  headers,
}: MobileSummaryHeaderCellProps<TData>) {
  const sortableHeaders = headers.filter((h) => h.column.getCanSort());

  const labelContent = headers.reduce<React.ReactNode[]>((acc, h, idx) => {
    const label =
      typeof h.column.columnDef.header === 'string'
        ? h.column.columnDef.header
        : h.id;

    // Only show sort indicator for the primary sorted column (sortIndex === 0)
    const sortDir = h.column.getIsSorted();
    const isPrimarySorted = sortDir && h.column.getSortIndex() === 0;
    const sortIndicator = isPrimarySorted ? (
      sortDir === 'desc' ? (
        <ArrowDown className="inline h-3 w-3" />
      ) : (
        <ArrowUp className="inline h-3 w-3" />
      )
    ) : null;

    if (idx === 0) {
      return [
        <span key={h.id}>
          {label}
          {sortIndicator}
        </span>,
      ];
    }

    return [
      ...acc,
      <span key={h.id} className="text-muted-foreground">
        , {label}
        {sortIndicator}
      </span>,
    ];
  }, []);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="-ml-3 h-8 gap-1 font-medium"
        >
          {labelContent}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {sortableHeaders.length === 0 ? (
          <DropdownMenuItem disabled className="text-muted-foreground">
            No sortable columns
          </DropdownMenuItem>
        ) : (
          sortableHeaders.map((header) => {
            const sort: false | SortDirection = header.column.getIsSorted();
            const sortIndex = header.column.getSortIndex();
            const resultDirection = getSortResultDirection(sort, sortIndex);
            const label =
              typeof header.column.columnDef.header === 'string'
                ? header.column.columnDef.header
                : header.id;

            return (
              <DropdownMenuItem
                key={header.id}
                onClick={() => {
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  (header.column as any).toggleMultiSort();
                }}
                className="gap-2"
              >
                {/* Show the RESULT direction (what clicking will do) */}
                {resultDirection === 'desc' ? (
                  <ArrowDown className="h-4 w-4" />
                ) : (
                  <ArrowUp className="h-4 w-4" />
                )}
                Sort by {label}{' '}
                {resultDirection === 'desc' ? 'descending' : 'ascending'}
              </DropdownMenuItem>
            );
          })
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
